import { AsyncPipe, NgFor, NgIf } from "@angular/common";
import { Component, OnDestroy, QueryList, ViewChild, ViewChildren, inject } from "@angular/core";
import { NoticeService } from "@app/services/notice.service";
import { LoadingIndicatorComponent } from "@app/shared/components/ui/loading-indicator";
import { NoticeResponse } from "@app/shared/models/notices/notice-response.model";
import { SwalComponent, SwalPortalTargets, SweetAlert2Module } from "@sweetalert2/ngx-sweetalert2";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { NoticeBoardItemComponent } from "./notice-board-item/notice-board-item.component";
import { FormsModule } from '@angular/forms';
import { IgnoreNoticeBoardFlagModel } from "@app/shared/models/notices/ignore-notice-board-flag.model";
import { AlertService } from "@app/services";


interface Notice {
  id: number;
  title: string;
  base64Image: string;
  contentText: string;
  moreInformationURL: string;
}

interface LaunchNoticeBoardOptions {
  onlyIfNoticesToShow: boolean;
}

type State = {
  loading: boolean,
  notices: Notice[],
  isEmptyNoticeList: boolean
};

@Component({
  selector: 'lpcr-notice-board',
  templateUrl: './notice-board.component.html',
  imports: [
    NgIf, AsyncPipe, NgFor,
    SweetAlert2Module,
    LoadingIndicatorComponent,
    NoticeBoardItemComponent,
    FormsModule
  ]
})
export class NoticeBoardComponent implements OnDestroy {

  public readonly swalTargets = inject(SwalPortalTargets);
  private readonly noticeService = inject(NoticeService);
  private readonly alertService = inject(AlertService);

  @ViewChildren('noticeBoardItem')
  public readonly noticeBoardItems!: QueryList<NoticeBoardItemComponent>;

  private readonly state = new BehaviorSubject<State>({
    loading: true,
    notices: [],
    isEmptyNoticeList: true,
  });

  public readonly state$: Observable<State> = this.state.asObservable();

  public checkboxIgnoreNoticeBoard!: boolean;

  @ViewChild('noticeBoardSwalModal')
  private readonly modale!: SwalComponent;

  private noticeInterval!: ReturnType<typeof setInterval>;
  private noticeItems!: NoticeBoardItemComponent[];
  private currentNoticeIndex!: number;
  
  public onClosed(): void {
    this.state.next({
      ...this.state.value,
      loading: true,
    });

    if (this.noticeInterval) {
      clearInterval(this.noticeInterval);
    }

  }

  public ngOnDestroy(): void {
    this.state.complete();

    if (this.noticeInterval) {
      clearInterval(this.noticeInterval);
    }
  }

  public async launch(launchNoticeBoardOptions: LaunchNoticeBoardOptions): Promise<void> {
    if (!launchNoticeBoardOptions.onlyIfNoticesToShow) {
      this.modale.fire();
    }

    const ignoreNoticeBoardFlagModel: IgnoreNoticeBoardFlagModel = await this.noticeService.getIgnoreNoticeBoardFlag();
    if (launchNoticeBoardOptions.onlyIfNoticesToShow && ignoreNoticeBoardFlagModel.ignoreNoticeBoardFlag) {
      return;
    }
    this.checkboxIgnoreNoticeBoard = ignoreNoticeBoardFlagModel.ignoreNoticeBoardFlag;
  
    const result: NoticeResponse[] = await this.noticeService.getNoticesToShow();

    const notices: Notice[] = result.map(x => ({
      id: x.id,
      title: x.title,
      base64Image: x.base64Image,
      contentText: x.contentText,
      moreInformationURL: x.moreInformationURL
    }));

    this.state.next({
      ...this.state.value,
      loading: false,
      notices: notices,
      isEmptyNoticeList: notices.length == 0
    });

    if (launchNoticeBoardOptions.onlyIfNoticesToShow && !this.state.value.isEmptyNoticeList) {
      this.modale.fire();
    }

    this.noticeItems = (await firstValueFrom(this.noticeBoardItems.changes)).toArray();

    if (this.noticeItems.length == 0) {
      return;
    }
    this.startCarousel(0);
  }


  public previousNotice(): void {
    this.noticeItems[this.currentNoticeIndex].hideNotice();
    const newNoticeIndex = this.currentNoticeIndex > 0 ? this.currentNoticeIndex - 1 : this.noticeItems.length - 1;
    this.startCarousel(newNoticeIndex);
  }

  public nextNotice(): void {

    this.noticeItems[this.currentNoticeIndex].hideNotice();
    this.startCarousel((this.currentNoticeIndex + 1) % this.noticeItems.length);
  }

  private startCarousel(initialNoticeIndex: number) {
    if (this.noticeInterval) {
      clearInterval(this.noticeInterval);
    }

    this.currentNoticeIndex = initialNoticeIndex;
    this.noticeItems[this.currentNoticeIndex].showNotice();
    this.noticeInterval = setInterval(() => {
      this.noticeItems[this.currentNoticeIndex].hideNotice();
      this.currentNoticeIndex = (this.currentNoticeIndex + 1) % this.noticeItems.length;
      this.noticeItems[this.currentNoticeIndex].showNotice();
    }, 10000);
  }

  public async onIgnoreNoticeBoardChange(): Promise<void> {
    try {
      await this.noticeService.updateIgnoreNoticeBoardFlag({ ignoreNoticeBoardFlag: this.checkboxIgnoreNoticeBoard });
    }
    catch (e) {
      console.error(e);
      await this.alertService.displayError('Erreur API', `Une erreur est survenue lors de la mise à jour de l'information.`);
    }

  }

  public close(): void {
    this.modale.close();
  }


}
